import React from 'react';
import { Button } from 'antd';
import CurrencyFormat from 'react-currency-format';
import * as reactstrp from 'reactstrap';
import { TrialBalanceBreakdown, trialBalanceAccountKey } from './journalReportUtils';

export function balanceSheetAccountKey(section, item) {
  return `${section}|${trialBalanceAccountKey(item)}`;
}

export function parseBalanceSheetSections(sections) {
  return (sections || []).map((cat) => ({
    ...cat,
    report: (cat.report || []).map((row) => ({
      ...row,
      breakdown: row.breakdown || [],
      breakdownLoaded: false,
      breakdownSummary: null,
      breakdownError: null,
    })),
  }));
}

export function mergeBreakdownIntoSection(
  sections,
  sectionKey,
  rowKey,
  breakdown,
  breakdownSummary,
  breakdownError = null
) {
  return sections.map((cat) => ({
    ...cat,
    report: (cat.report || []).map((row) =>
      balanceSheetAccountKey(sectionKey, row) === rowKey
        ? { ...row, breakdown, breakdownLoaded: true, breakdownSummary, breakdownError }
        : row
    ),
  }));
}

const sectionStateKey = {
  assets: 'balancesheet_report_assets',
  equity: 'balancesheet_report_equity',
  liability: 'balancesheet_report_liability',
};

export function getBalanceSheetSectionStateKey(sectionKey) {
  return sectionStateKey[sectionKey] || sectionStateKey.assets;
}

export function BalanceSheetCategoryTable({
  catgry,
  sectionKey,
  viewOpeningBalance,
  expandedAccountKey,
  breakdownLoadingKey,
  reportBasis,
  onToggleBreakdown,
  calculateTotalDebit,
  calculateTotalCredit,
}) {
  if (!catgry || !catgry.report || catgry.report.length === 0) {
    return null;
  }

  const colSpan = viewOpeningBalance ? 5 : 4;

  return (
    <reactstrp.Table bordered className="balance-sheet-account-cols">
      <thead>
        <tr>
          <th className="bs-breakdown-col">Breakdown</th>
          <th className="bs-account-col">Account</th>
          {viewOpeningBalance ? <th className="bs-opening-col align-right">Opening Balance</th> : null}
          <th className="bs-amount-col align-right">Debit</th>
          <th className="bs-amount-col align-right">Credit</th>
        </tr>
      </thead>
      <tbody>
        {catgry.report.map((item) => {
          const rowKey = balanceSheetAccountKey(sectionKey, item);
          const isExpanded = expandedAccountKey === rowKey;
          const isLoading = breakdownLoadingKey === rowKey;

          return (
            <React.Fragment key={rowKey}>
              <tr>
                <td className="bs-breakdown-col">
                  <Button
                    size="small"
                    loading={isLoading}
                    onClick={() => onToggleBreakdown(item, sectionKey)}
                  >
                    {isExpanded ? 'Hide' : 'Show'}
                  </Button>
                </td>
                <td className="bs-account-col">{item.account}</td>
                {viewOpeningBalance ? (
                  <td className="bs-opening-col align-right">
                    <CurrencyFormat value={item.total_inst_balance} displayType="text" thousandSeparator />
                  </td>
                ) : null}
                <td className="bs-amount-col align-right">
                  <CurrencyFormat value={item.debit} displayType="text" thousandSeparator />
                </td>
                <td className="bs-amount-col align-right">
                  <CurrencyFormat value={item.credit} displayType="text" thousandSeparator />
                </td>
              </tr>
              {isExpanded ? (
                <tr>
                  <td colSpan={colSpan} style={{ padding: 0 }}>
                    <TrialBalanceBreakdown
                      breakdown={item.breakdown}
                      reportBasis={reportBasis}
                      summary={item.breakdownSummary}
                      errorDetail={item.breakdownError}
                      basisTitle="How the balance sheet figure is built"
                      summaryLabel="Balance sheet"
                      balanceLabel="Balance"
                    />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          );
        })}

        <tr>
          <td className="bs-breakdown-col" style={{ fontWeight: 'bolder' }}>Total</td>
          <td className="bs-account-col" />
          {viewOpeningBalance ? <td className="bs-opening-col" /> : null}
          <td className="bs-amount-col align-right" style={{ fontWeight: 'bolder' }}>
            <CurrencyFormat
              value={calculateTotalDebit(catgry.report)}
              displayType="text"
              thousandSeparator
            />
          </td>
          <td className="bs-amount-col align-right" style={{ fontWeight: 'bolder' }}>
            <CurrencyFormat
              value={calculateTotalCredit(catgry.report)}
              displayType="text"
              thousandSeparator
            />
          </td>
        </tr>
      </tbody>
    </reactstrp.Table>
  );
}
